import _ from 'lodash/fp'
import { delay } from 'redux-saga'
import { put, call, take, takeLatest } from 'redux-saga/effects'
import { makeSaga } from './util'
import { postEffect, getEffect } from './network'

import * as connectedUnionsReducer from '../reducers/connectedUnions'
import * as profileReducer from '../reducers/profile'
import * as gamesMatchesReducer from '../reducers/matches/games'

//  ███████╗███████╗███████╗███████╗ ██████╗████████╗███████╗
//  ██╔════╝██╔════╝██╔════╝██╔════╝██╔════╝╚══██╔══╝██╔════╝
//  █████╗  █████╗  █████╗  █████╗  ██║        ██║   ███████╗
//  ██╔══╝  ██╔══╝  ██╔══╝  ██╔══╝  ██║        ██║   ╚════██║
//  ███████╗██║     ██║     ███████╗╚██████╗   ██║   ███████║
//  ╚══════╝╚═╝     ╚═╝     ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝

//  ███████╗████████╗ ██████╗ ██████╗ ██╗
//  ██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗██║
//  ███████╗   ██║   ██║   ██║██████╔╝██║
//  ╚════██║   ██║   ██║   ██║██╔═══╝ ╚═╝
//  ███████║   ██║   ╚██████╔╝██║     ██╗
//  ╚══════╝   ╚═╝    ╚═════╝ ╚═╝     ╚═╝
//  This connection method is not used for OAUTH (ie. GolfBox)
//  If the organization you want to connect to uses OAUTH,
//  you could probably use a library like https://github.com/FormidableLabs/react-native-app-auth
//  but if the org uses simple username/password authentication, you could use this (after you
//  have implemented the new connection in the API)
// Match the generic connect target or whatever
export function * connectEffect (payload) {
  const { union, username, password } = payload
  const request = {
    url: `organizations/${union}/connect`,
    options: {
      body: {
        username,
        password
      }
    }
  }
  const chan = yield call(postEffect, request)
  return yield take(chan)
}

export function * disconnectEffect (payload) {
  const { union } = payload
  const request = {
    url: `organizations/${union}/disconnect`
  }
  const chan = yield call(postEffect, request)
  return yield take(chan)
}

export function * syncUserInfoEffect (union) {
  const request = {
    url: `organizations/${union}/synchronize`
  }
  const chan = yield call(postEffect, request)
  return yield take(chan)
}

export function * submitScorecardEffect ({ gameId, marker, union }) {
  const request = {
    url: `organizations/${union}/scorecard`,
    options: {
      body: {
        marker,
        gameId
      }
    }
  }
  const chan = yield call(postEffect, request)
  return yield take(chan)
}

export function * getOrganizationsEffect () {
  const request = {
    url: 'organizations'
  }
  const chan = yield call(getEffect, request)
  return yield take(chan)
}

//  ███████╗ █████╗  ██████╗  █████╗ ███████╗
//  ██╔════╝██╔══██╗██╔════╝ ██╔══██╗██╔════╝
//  ███████╗███████║██║  ███╗███████║███████╗
//  ╚════██║██╔══██║██║   ██║██╔══██║╚════██║
//  ███████║██║  ██║╚██████╔╝██║  ██║███████║
//  ╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚══════╝

function * refetchUserSideEffect () {
  yield put(profileReducer.actions.loadRequest())
  const newProfile = yield take([profileReducer.types.LOAD_SUCCESS, profileReducer.types.LOAD_ERROR])
  if (_.get('type', newProfile) === profileReducer.types.LOAD_SUCCESS) {
    yield put(profileReducer.actions.loadError())
  } else {
    yield put(profileReducer.actions.loadSuccess())
  }
}

function * syncUserInfoFlow ({ payload, retries = 2 }) {
  const { union } = payload

  // wait a short while before calling sync effect
  yield delay(30)
  const result = yield call(syncUserInfoEffect, union)

  // Check the result of the effect
  if (_.get('status', result) === 'unauthorized') {
    // We have a 401
    if (retries === 0) {
      // We are on our last attempt, put the errors
      yield put(connectedUnionsReducer.actions.setToast({ text: `Could not authenticate with ${union}`, severity: 'error' }))
      yield put(connectedUnionsReducer.actions.toggleModal())
      yield put(connectedUnionsReducer.actions.syncUserInfoError({ error: 'unauthorized' }))
    } else if (retries > 1) {
      // We are still trying, return a recursive call with -1 retries
      return yield syncUserInfoFlow({ payload, retries: retries - 1 })
    }
  } else if (result.error) {
    // Any old error occurred
    if (retries === 0) {
      // We are on our last attempt, put the errors
      // TODO distinguise the error, us or them
      yield put(connectedUnionsReducer.actions.setToast({ text: `Could not fetch user info from ${union}`, severity: 'error' }))
      yield put(connectedUnionsReducer.actions.syncUserInfoError(result.error))
    } else if (retries > 1) {
      // We are still trying, return a recursive call with -1 retries
      return yield syncUserInfoFlow({ payload, retries: retries - 1 })
    }
  } else {
    // No errors put success
    yield call(refetchUserSideEffect)
    yield put(connectedUnionsReducer.actions.syncUserInfoSuccess())
    yield put(connectedUnionsReducer.actions.setToast({ text: `Successful fetch of user info from ${union}`, severity: 'success' }))
  }
}

function * connectRequestFlow ({ payload }) {
  const { union } = payload
  const result = yield call(connectEffect, payload)
  const error = _.get('error', result)
  if (error) {
    yield put(connectedUnionsReducer.actions.setToast({ text: `Could not authenticate with ${union}`, severity: 'error' }))
    yield put(connectedUnionsReducer.actions.connectError(error))
  } else {
    yield call(syncUserInfoFlow, { payload: { union } })
    yield put(connectedUnionsReducer.actions.connectSuccess())
    yield put(connectedUnionsReducer.actions.setToast({ text: `Successful auhtentication with ${union}`, severity: 'success' }))
  }
}

function * disconnectRequestFlow ({ payload }) {
  const { union } = payload
  const result = yield call(disconnectEffect, payload)
  const error = _.get('error', result)
  if (error) {
    yield put(connectedUnionsReducer.actions.setToast({ text: `Could not authenticate with ${union}`, severity: 'error' }))
    yield put(connectedUnionsReducer.actions.disconnectError(error))
  } else {
    yield put(connectedUnionsReducer.actions.setToast({ text: `Successful auhtentication with ${union}`, severity: 'success' }))
    yield put(connectedUnionsReducer.actions.disconnectSuccess())
    yield call(refetchUserSideEffect)
  }
}

function * submitScorecardFlow ({ payload }) {
  const { gameId, marker, union } = payload
  if (!gameId) {
    return yield put(connectedUnionsReducer.actions.submitError({ error: 'no game id supplied' }))
  }

  // The server is assumed to know about the game.
  const submitResult = yield call(submitScorecardEffect, { gameId, marker, union })
  if (_.get('status', submitResult) === 'unauthorized') {
    yield put(connectedUnionsReducer.actions.setToast({ text: `Could not authenticate with ${union}`, severity: 'error' }))
    yield put(connectedUnionsReducer.actions.toggleModal())
    return yield put(connectedUnionsReducer.actions.submitError({ error: 'unauthorized' }))
  }

  const submitError = _.get('error', submitResult)
  if (submitError) {
    // TODO distinguise the error, us or them
    yield put(connectedUnionsReducer.actions.setToast({ text: `Submit to ${union} errored`, severity: 'error' }))
    return yield put(connectedUnionsReducer.actions.submitError(submitError))
  }

  // Finish the game and wait for it to finish
  yield put(gamesMatchesReducer.actions.finishRequest({ gameId }))
  yield take(gamesMatchesReducer.types.FINISH)

  // Tell the app that the submition is successfu
  yield put(connectedUnionsReducer.actions.setToast({ text: `Successful submit to ${union}`, severity: 'success' }))
  yield put(connectedUnionsReducer.actions.submitSuccess())

  // Go to top of current stack and go to games list - which should still be paginated!
}

function * getOrganizationsFlow () {
  const organizations = yield call(getOrganizationsEffect)
  if (organizations.error) {
    yield put(connectedUnionsReducer.actions.getOrganizationsError(organizations.error))
  } else {
    yield put(connectedUnionsReducer.actions.getOrganizationsSuccess(organizations))
  }
}

export default makeSaga([
  takeLatest(connectedUnionsReducer.types.CONNECT_REQUEST, connectRequestFlow),
  takeLatest(connectedUnionsReducer.types.DISCONNECT_REQUEST, disconnectRequestFlow),
  takeLatest(connectedUnionsReducer.types.SUBMIT_REQUEST, submitScorecardFlow),
  takeLatest(connectedUnionsReducer.types.SYNC_USER_INFO_REQUEST, syncUserInfoFlow),
  takeLatest(connectedUnionsReducer.types.GET_ORGANIZATIONS_REQUEST, getOrganizationsFlow)
])
